import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {OttopilotEventContainer} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";

export const NOTES_BODY_MAX_PLAIN_LENGTH = 500;
export const NOTES_MAX_ATTACHMENTS = 2;

export type NoteAttachment = {
    assetId: Uuid;
    fileName: string;
    mimeType?: string;
    createdAt: Iso8601DateTime;
};

export type Note = {
    id: Uuid;
    eventContainerId: Uuid;
    bodyMarkdown: string;
    bodyPlainTextLength: number;
    createdBy: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    attachments: Array<NoteAttachment>;
} & (
    | {scopeType: "event"; eventId: Uuid; eventTemplateId: Uuid}
    | {scopeType: "eventContainer"; eventId: null; eventTemplateId: null}
);

export type NoteAuthor = {
    displayName: string;
    roleLabel: string;
    avatarUrl?: string;
};

export type NoteListItem = Note & {
    author: NoteAuthor;
    sourceLabel: string;
};

export type NotesEventRow = {
    id: Uuid;
    templateId: Uuid;
    createdBy: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    additionalColumnValues: Record<Uuid, unknown>;
    notes?: unknown;
};

export type ListNotesCreateScope =
    | {scopeType: "eventContainer"}
    | {scopeType: "event"; eventId: Uuid; eventTemplateId: Uuid};

export type NoteSortDirection = "recent" | "oldest";

export function markdownPlainTextLength(markdown: string): string {
    return markdown
        .replace(/```[\s\S]*?```/g, " ")
        .replace(/`[^`]*`/g, " ")
        .replace(/!\[[^\]]*]\([^)]*\)/g, " ")
        .replace(/\[[^\]]*]\([^)]*\)/g, " ")
        .replace(/[#>*_~-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

export function computeNotePlainTextLength(markdown: string): number {
    return markdownPlainTextLength(markdown).length;
}

/** True when a note was saved after its initial create (edit), not on first write. */
export function isNoteEdited(note: Pick<Note, "createdAt" | "updatedAt">): boolean {
    const createdMs = Date.parse(note.createdAt);
    const updatedMs = Date.parse(note.updatedAt);
    if (Number.isNaN(createdMs) === true || Number.isNaN(updatedMs) === true) {
        return false;
    }
    return updatedMs - createdMs > 1000;
}

export function parseNotesColumnValue(raw: unknown): Array<Note> {
    let normalizedRaw: unknown = raw;
    if (typeof normalizedRaw === "string") {
        const trimmed = normalizedRaw.trim();
        if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
            try {
                normalizedRaw = JSON.parse(trimmed) as unknown;
            } catch {
                return [];
            }
        } else if (trimmed === "") {
            return [];
        }
    }
    if (normalizedRaw === null || normalizedRaw === undefined) {
        return [];
    }
    const fallbackTime = new Date().toISOString() as Iso8601DateTime;
    if (Array.isArray(normalizedRaw) === true) {
        return normalizedRaw
            .map((item): Note | null => parseNoteRecord(item, fallbackTime))
            .filter((note): note is Note => note !== null);
    }
    if (typeof normalizedRaw === "object") {
        return Object.values(normalizedRaw as Record<string, unknown>)
            .map((item): Note | null => parseNoteRecord(item, fallbackTime))
            .filter((note): note is Note => note !== null);
    }
    return [];
}

function readString(record: Record<string, unknown>, ...keys: Array<string>): string | null {
    for (const key of keys) {
        const value = record[key];
        if (typeof value === "string" && value.trim() !== "") {
            return value;
        }
    }
    return null;
}

function readUuid(record: Record<string, unknown>, ...keys: Array<string>): Uuid | null {
    const value = readString(record, ...keys);
    return value !== null ? (value as Uuid) : null;
}

function parseNoteAttachment(raw: unknown, fallbackTime: Iso8601DateTime): NoteAttachment | null {
    if (typeof raw !== "object" || raw === null) {
        return null;
    }
    const record = raw as Record<string, unknown>;
    const assetId = readUuid(record, "assetId", "asset_id", "id");
    if (assetId === null) {
        return null;
    }
    const fileName = readString(record, "fileName", "file_name") ?? "Attachment";
    const createdAt =
        readString(record, "createdAt", "created_at", "uploadedAt", "uploaded_at") ?? fallbackTime;
    const mimeType = readString(record, "mimeType", "mime_type") ?? undefined;
    return {
        assetId,
        fileName,
        mimeType,
        createdAt: createdAt as Iso8601DateTime,
    };
}

function parseNoteRecord(raw: unknown, fallbackTime: Iso8601DateTime): Note | null {
    if (typeof raw !== "object" || raw === null) {
        return null;
    }
    const record = raw as Record<string, unknown>;
    const id = readUuid(record, "id");
    const eventContainerId = readUuid(record, "eventContainerId");
    const createdBy = readUuid(record, "createdBy", "created_by");
    if (id === null || eventContainerId === null || createdBy === null) {
        return null;
    }
    const bodyMarkdown = readString(record, "bodyMarkdown", "body_markdown", "body") ?? "";
    const bodyPlainTextLengthRaw = record.bodyPlainTextLength ?? record.body_plain_text_length;
    const bodyPlainTextLength =
        typeof bodyPlainTextLengthRaw === "number"
            ? bodyPlainTextLengthRaw
            : computeNotePlainTextLength(bodyMarkdown);
    const createdAt = readString(record, "createdAt", "created_at") ?? fallbackTime;
    const updatedAt = readString(record, "updatedAt", "updated_at") ?? createdAt;
    const attachmentsRaw = record.attachments;
    const attachments = Array.isArray(attachmentsRaw)
        ? attachmentsRaw
              .map((item) => parseNoteAttachment(item, fallbackTime))
              .filter((item): item is NoteAttachment => item !== null)
        : [];

    const scopeTypeRaw = readString(record, "scopeType", "scope_type");
    const eventId = readUuid(record, "eventId", "event_id");
    const eventTemplateId = readUuid(record, "eventTemplateId", "event_template_id", "templateId", "template_id");

    if (scopeTypeRaw === "event" || (eventId !== null && eventTemplateId !== null)) {
        if (eventId === null || eventTemplateId === null) {
            return null;
        }
        return {
            id,
            eventContainerId,
            bodyMarkdown,
            bodyPlainTextLength,
            createdBy,
            createdAt: createdAt as Iso8601DateTime,
            updatedAt: updatedAt as Iso8601DateTime,
            attachments,
            scopeType: "event",
            eventId,
            eventTemplateId,
        };
    }

    return {
        id,
        eventContainerId,
        bodyMarkdown,
        bodyPlainTextLength,
        createdBy,
        createdAt: createdAt as Iso8601DateTime,
        updatedAt: updatedAt as Iso8601DateTime,
        attachments,
        scopeType: "eventContainer",
        eventId: null,
        eventTemplateId: null,
    };
}

function serializeNoteForApi(note: Note): Record<string, unknown> {
    return {
        id: note.id,
        eventContainerId: note.eventContainerId,
        bodyMarkdown: note.bodyMarkdown,
        bodyPlainTextLength: note.bodyPlainTextLength,
        createdBy: note.createdBy,
        createdAt: note.createdAt,
        updatedAt: note.updatedAt,
        scopeType: note.scopeType,
        ...(note.scopeType === "event"
            ? {eventId: note.eventId, eventTemplateId: note.eventTemplateId}
            : {eventId: null, eventTemplateId: null}),
        attachments: note.attachments.map((attachment) => ({
            assetId: attachment.assetId,
            fileName: attachment.fileName,
            mimeType: attachment.mimeType ?? null,
            createdAt: attachment.createdAt,
        })),
    };
}

/** Backend `notes` fixed field is a map keyed by note id (not a JSON array). */
export function serializeNotesForApi(notes: Array<Note>): Record<string, unknown> {
    return Object.fromEntries(notes.map((note) => [note.id, serializeNoteForApi(note)]));
}

export function readEventContainerNotes(
    eventContainer: Pick<OttopilotEventContainer, "notes">,
): Array<Note> {
    return parseNotesColumnValue(eventContainer.notes);
}

export function readEventNotes(event: Pick<NotesEventRow, "notes">): Array<Note> {
    return parseNotesColumnValue(event.notes);
}

export function resolveEventTemplateSourceLabel(
    eventTemplateId: Uuid,
    eventTemplates: Array<Pick<OttopilotEventTemplate, "id" | "configuration">>,
    moduleLabel?: string,
): string {
    const template = eventTemplates.find((item) => String(item.id) === String(eventTemplateId));
    if (template !== undefined) {
        return template.configuration.displayNameVariants.singularSentenceCase;
    }
    return moduleLabel ?? "Event";
}

export function collectEventContainerNoteListItems(args: {
    eventContainer: OttopilotEventContainer;
    eventTemplates: Array<Pick<OttopilotEventTemplate, "id" | "configuration">>;
    resolveAuthor: (userId: Uuid) => NoteAuthor;
    eventContainerSourceLabel?: string;
    sortDirection?: NoteSortDirection;
}): Array<NoteListItem> {
    const eventContainerSourceLabel = args.eventContainerSourceLabel ?? "Event Container";
    const items: Array<NoteListItem> = [];

    for (const note of readEventContainerNotes(args.eventContainer)) {
        const sourceLabel =
            note.scopeType === "event"
                ? resolveEventTemplateSourceLabel(note.eventTemplateId, args.eventTemplates)
                : eventContainerSourceLabel;
        items.push({
            ...note,
            author: args.resolveAuthor(note.createdBy),
            sourceLabel,
        });
    }

    const direction = args.sortDirection ?? "recent";
    items.sort((a, b) => {
        const aMs = Date.parse(a.createdAt);
        const bMs = Date.parse(b.createdAt);
        return direction === "recent" ? bMs - aMs : aMs - bMs;
    });

    return items;
}

export function createNoteDraft(args: {
    eventContainerId: Uuid;
    createScope: ListNotesCreateScope;
    bodyMarkdown: string;
    attachments: Array<NoteAttachment>;
    createdBy: Uuid;
}): Note {
    const now = new Date().toISOString() as Iso8601DateTime;
    const bodyPlainTextLength = computeNotePlainTextLength(args.bodyMarkdown);
    const base = {
        id: generateUuid(),
        eventContainerId: args.eventContainerId,
        bodyMarkdown: args.bodyMarkdown,
        bodyPlainTextLength,
        createdBy: args.createdBy,
        createdAt: now,
        updatedAt: now,
        attachments: args.attachments,
    };
    if (args.createScope.scopeType === "event") {
        return {
            ...base,
            scopeType: "event",
            eventId: args.createScope.eventId,
            eventTemplateId: args.createScope.eventTemplateId,
        };
    }
    return {
        ...base,
        scopeType: "eventContainer",
        eventId: null,
        eventTemplateId: null,
    };
}

export function upsertNoteInList(notes: Array<Note>, nextNote: Note): Array<Note> {
    const index = notes.findIndex((note) => note.id === nextNote.id);
    if (index === -1) {
        return [...notes, nextNote];
    }
    const copy = [...notes];
    copy[index] = nextNote;
    return copy;
}

export function removeNoteFromList(notes: Array<Note>, noteId: Uuid): Array<Note> {
    return notes.filter((note) => note.id !== noteId);
}
